import React, { useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  CircleUserRound,
  Landmark,
  X,
} from "lucide-react";
import { Label, TextInput } from "flowbite-react";
import { gamesApi } from "../../api/games";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";
export default function ModifyGame({ isOpen, onClose, onSave, gameData, allGameSettings }) {
  const { tokens } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedGame, setSelectedGame] = useState("");
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Initialize form states with gameData
  const [name, setName] = useState(gameData?.name || "");
  const [commissionRate, setCommissionRate] = useState(gameData?.commissionRate || "");
  const [winRate, setWinRate] = useState(gameData?.winRate || "");
  const [doubleWinRate, setDoubleWinRate] = useState(gameData?.doubleWin || "");
  const [tripleWinRate, setTripleWinRate] = useState(gameData?.tripleWin || "");
  const [gameSettingsId, setGameSettingsId] = useState(gameData?.game_settings_id || "");
  const [gameId, setGameId] = useState(gameData?.id || "");
  const [isPublic, setIsPublic] = useState(gameData?.public || false);

  useEffect(() => {
    const game = allGameSettings.find(game => game.id === gameData?.game_settings_id);
    setSelectedGame(game?.name || "");
  }, [allGameSettings, gameData?.game_settings_id]);

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      setTimeout(() => setMounted(true), 10);
    } else {
      setMounted(false);
      setTimeout(() => setVisible(false), 300);
    }
  }, [isOpen]);

  if (!visible) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      name,
      commissionRate,
      winRate,
      doubleWin: parseInt(doubleWinRate),
      tripleWin: parseInt(tripleWinRate),
      gameSettingsId,
      isPublic: isPublic,
    });
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center px-4 z-[60] font-poppins" 
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className={`w-full max-w-sm bg-white rounded-3xl transform transition-transform duration-300 ${
          mounted ? "translate-y-0" : "translate-y-full"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className=" py-5 bg-[#0E64D1] rounded-t-3xl text-white">
          <h2 className="font-bold text-center">Modify Game</h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-4">
          <div className="my-3">
            <Label htmlFor="name">Name</Label>
            <TextInput
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md"
            />
          </div>

          <div className="my-3 relative">
            <Label
              htmlFor="game"
              className="block text-sm text-[#695C5C] font-poppins ml-1 mb-1"
            >
              Select Game{" "}
            </Label>

            <button
              id="game"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setDropdownOpen(!dropdownOpen);
              }}
              className="w-full text-sm px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none flex justify-between"
            >
              <span className="font-light text-sm text-gray-500">
                {selectedGame || "Please Select Game"}
              </span>
              {dropdownOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>

            {dropdownOpen && (
              <ul className="absolute w-full mt-1 bg-white border rounded-md shadow-md z-10"
                onClick={(e) => e.stopPropagation()}
              >
                  {allGameSettings.map((game, index) => (
                    <li
                      key={game.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedGame(game.name);
                        setGameSettingsId(game.id);
                        setDropdownOpen(false);
                      }}
                      className={`p-2.5 text-xs text-black hover:bg-[#E1E7EF] cursor-pointer ${
                        index !== 0 ? "border-t border-[#E1E7EF]" : ""
                      }`}
                    >
                      {game.name.toUpperCase()}
                    </li>
                  ))}
              </ul>
            )}
          </div>

          <div className="my-3">
            <Label>Commission Rate</Label>
            <TextInput
              value={commissionRate}
              onChange={(e) => setCommissionRate(e.target.value)}
              className="w-full rounded-md"
            />
          </div>

          <div className="my-3">
            <Label>Win Rate</Label>
            <TextInput
              value={winRate}
              onChange={(e) => setWinRate(e.target.value)}
              className="w-full rounded-md"
            />
          </div>

          <div className="my-3">
            <Label
              htmlFor="phoneNumber"
              className="block text-sm text-[#695C5C] font-poppins ml-1 mb-1"
            >
              Double Win Rate{" "}
            </Label>
            <TextInput
              value={doubleWinRate}
              onChange={(e) => setDoubleWinRate(e.target.value)}
              className="w-full rounded-md"
            />
          </div>

          <div className="my-3">
            <Label
              htmlFor="phoneNumber"
              className="block text-sm text-[#695C5C] font-poppins ml-1 mb-1"
            >
              Triple Win Rate
            </Label>
            <TextInput
              value={tripleWinRate}
              onChange={(e) => setTripleWinRate(e.target.value)}
              className="w-full rounded-md"
            />
          </div>

          <div className="my-3">
            <Label className="block text-sm text-[#695C5C] font-poppins ml-1 mb-1">
              Game Visibility
            </Label>
            <div className="mt-2 flex items-center">
              <button
                type="button"
                onClick={() => setIsPublic(!isPublic)}
                className={`flex items-center justify-between rounded-full px-1 w-20 h-6 ${
                  isPublic
                    ? "bg-blue-500"
                    : "bg-gray-300 transition-colors duration-300"
                }`}
              >
                {isPublic ? (
                  <>
                    <span
                      className={`text-[10px] font-normal ps-2 text-white translate-x-0 transition-transform duration-300 `}
                    >
                      Public
                    </span>
                    <div
                      className={`w-4 h-4 rounded-full bg-white shadow-md transform transition-transform `}
                    ></div>
                  </>
                ) : (
                  <>
                    <div
                      className={`w-4 h-4 rounded-full bg-white shadow-md transform transition-transform `}
                    ></div>
                    <span
                      className={`text-[10px] font-normal pe-2 text-gray-800 translate-x-0`}
                    >
                      Private
                    </span>
                  </>
                )}
              </button>
              <span className="ml-2 text-sm text-gray-600">
                {isPublic ? "Game will be visible to public users" : "Game will only be visible to admins"}
              </span>
            </div>
          </div>

          <div className="flex gap-3 mb-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 p-2 border border-blue-600 text-blue-600 text-sm rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              className="flex-1 p-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-600 transition-colors"
              onClick={handleSubmit}
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
